import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Mail } from "lucide-react";
import LegalFooter from "@/components/LegalFooter";

const FEATURES = [
  "Daily AI-triaged scan of parent emails",
  "Structured Google Sheet log in your Drive",
  "AI reply drafts with Warm / Direct / Detailed tones",
  "Weekly email digest",
  "Two-way sync between the app and your Sheet",
  "Unlimited entries · cancel anytime",
];

export default function Pricing() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const autoFired = useRef(false);
  const pingedRef = useRef(false);

  // Capture ?ref=… or ?utm_source=… into localStorage and ping backend once per mount.
  useEffect(() => {
    if (pingedRef.current) return;
    const incoming = params.get("ref") || params.get("utm_source");
    if (!incoming) return;
    const cleaned = incoming.replace(/[^a-zA-Z0-9_\-.]/g, "").slice(0, 64);
    if (!cleaned) return;
    pingedRef.current = true;
    try { localStorage.setItem("ca:ref", cleaned); } catch { /* ignore */ }
    api.post("/attribution/ping", { ref: cleaned, page: "pricing" }).catch(() => { /* ignore */ });
  }, [params]);

  const readStoredRef = useCallback(() => {
    try { return localStorage.getItem("ca:ref") || undefined; } catch { return undefined; }
  }, []);

  const startCheckout = useCallback(async (plan) => {
    if (!user) {
      try { localStorage.setItem("ca:auto-checkout", plan); } catch { /* ignore */ }
      nav("/");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { plan, ref: readStoredRef() });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  }, [user, nav, readStoredRef]);

  // Auto-checkout deep-link: /pricing?plan=annual&auto=1 (Wix CTA)
  useEffect(() => {
    if (autoFired.current || loading) return;
    const plan = params.get("plan");
    const auto = params.get("auto");
    if (auto !== "1" || (plan !== "monthly" && plan !== "annual")) return;
    autoFired.current = true;
    if (user) {
      startCheckout(plan);
    } else {
      try { localStorage.setItem("ca:auto-checkout", plan); } catch { /* ignore */ }
      nav("/", { replace: true });
    }
  }, [user, loading, params, startCheckout, nav]);

  return (
    <div className="min-h-screen w-full flex flex-col">
      <div className="flex-1 w-full px-6 py-14">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#971a8c] text-white flex items-center justify-center">
              <Mail className="h-4 w-4" />
            </div>
            <span className="font-heading text-lg text-[#1C2321]">ContactAssist</span>
          </Link>
          {user && (
            <button data-testid="pricing-back-btn" onClick={() => nav(-1)} className="text-sm text-[#5D6D63] hover:text-[#1C2321]">← Back</button>
          )}
        </div>

        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-3">Simple, honest pricing</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1C2321] font-medium mb-4">Try free for 7 days</h1>
          <p className="text-base text-[#5D6D63] max-w-xl mx-auto">
            Start your trial with a card on file — cancel any time before day 7 and you won&apos;t be charged.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto" data-testid="pricing-plans">
          <PlanCard
            testid="plan-monthly"
            title="Monthly"
            price="$7"
            cadence="/ month"
            highlight={false}
            cta="Start 7-day free trial"
            onClick={() => startCheckout("monthly")}
            busy={busy}
          />
          <PlanCard
            testid="plan-annual"
            title="Annual"
            price="$60"
            cadence="/ year"
            highlight
            savings="Save $24 · ~29% off"
            cta="Start 7-day free trial"
            onClick={() => startCheckout("annual")}
            busy={busy}
          />
        </div>

        <div className="mt-14 max-w-3xl mx-auto bg-white border border-[#E8E6DF] rounded-lg p-8">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-[#971a8c]" />
            <h2 className="font-heading text-2xl text-[#1C2321] font-medium">What&apos;s included</h2>
          </div>
          <ul className="space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-[#1C2321]">
                <Check className="h-4 w-4 mt-1 text-[#26abe2] shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-10 text-center text-xs text-[#8B9891]">
          Payment processed securely by Stripe. Cancel anytime from Settings.
        </p>
      </div>
      </div>
      <LegalFooter />
    </div>
  );
}

function PlanCard({ title, price, cadence, highlight, savings, cta, onClick, busy, testid }) {
  return (
    <div data-testid={testid} className={`relative rounded-2xl border p-8 transition-shadow bg-white ${
      highlight ? "border-[#971a8c] shadow-md" : "border-[#E8E6DF]"
    }`}>
      {highlight && (
        <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#ec197f] text-white text-xs font-semibold tracking-wider uppercase">
          Best value
        </div>
      )}
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891]">{title}</p>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-heading text-5xl text-[#1C2321] font-medium tabular-nums">{price}</span>
        <span className="text-[#5D6D63] text-sm">{cadence}</span>
      </div>
      {savings && <p className="mt-1 text-sm text-[#26abe2] font-medium">{savings}</p>}
      <Button
        data-testid={`${testid}-cta`}
        onClick={onClick}
        disabled={busy}
        className={`w-full mt-8 h-11 rounded-md text-white transition-colors ${
          highlight ? "bg-[#971a8c] hover:bg-[#7d1574]" : "bg-[#1C2321] hover:bg-black"
        }`}
      >
        {busy ? "Loading…" : cta}
      </Button>
      <p className="mt-3 text-xs text-[#8B9891] text-center">7 days free · cancel any time</p>
    </div>
  );
}
